import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Select, Input } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { storeComment, updateComment, editImport } from '@app/api/import.api';
import { Comment } from '@app/models/import/ImportResponse';

interface CommentsProps {
  commentData: any[];
  editable: boolean;
  status: string | null;
  import_id: string | null;
}

const CommentsTab: React.FC<CommentsProps> = ({ commentData, editable, import_id, status }) => {
  const [comments, setComments] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingComment, setEditingComment] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);

  // useEffect(() => {
  //   const fetchComments = async () => {
  //     if (!import_id) return;

  //     try {
  //       const response = await editImport(Number(import_id));

  //       // Log the entire response
  //       console.log('Full API Response:', JSON.stringify(response, null, 2));

  //       // Adjust how we access the data
  //       const importData = response?.import;

  //       console.log('Checking import:', importData);

  //       if (!importData) {
  //         console.warn('import is undefined, check API response structure.');
  //         return;
  //       }

  //       const comments = importData.import_comments || [];
  //       console.log('Extracted Comments:', comments);

  //       if (comments.length > 0) {
  //         const user = JSON.parse(localStorage.getItem('user') || '{}');
  //         console.log('Logged-in User:', user);

  //         const formattedComments = comments.map((comment: { updated_at: string | number | Date; user_id: any }) => ({
  //           ...comment,
  //           updated_at: new Date(comment.updated_at).toLocaleString(),
  //           user: comment.user_id === user.id ? user.name : 'Admin',
  //         }));

  //         console.log('Formatted Comments:', formattedComments);
  //         setComments(formattedComments);
  //       } else {
  //         console.warn('No comments found.');
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch comments:', error);
  //     }
  //   };

  //   fetchComments();
  // }, [import_id]);

  const user = JSON.parse(localStorage.getItem('user') || '{}')?.name || 'Unknown User';

  const handleAddOrEditComment = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const newComment = {
        comment_for: values.commentFor,
        comment: values.comment,
      };

      let updatedComments = [...comments];
      let res;

      if (editingComment) {
        res = await updateComment(Number(import_id), editingComment.id, newComment);
        updatedComments = updatedComments.map((comment) =>
          comment.id === editingComment.id
            ? { ...comment, ...newComment, updated_at: new Date().toLocaleString() }
            : comment,
        );
      } else {
        res = await storeComment(Number(import_id), newComment);

        // Fetch fresh comments from API to ensure correct structure
        const response = await editImport(Number(import_id));
        const importData = response?.import;
        if (importData) {
          updatedComments = importData.import_comments.map((comment: any) => ({
            ...comment,
            updated_at: new Date(comment.updated_at).toLocaleString(),
            user: comment.user_id === JSON.parse(localStorage.getItem('user') || '{}').id ? user : 'Admin',
          }));
        }
      }

      setComments(updatedComments);
      setIsModalVisible(false);
      setEditingComment(null);
      form.resetFields();
    } catch (error) {
      console.error('Failed to add or edit comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: any) => {
    setEditingComment(record);
    setIsModalVisible(true);
    form.setFieldsValue({
      commentFor: record.comment_for,
      comment: record.comment,
    });
  };

  const columns = [
    { title: 'Date & Time', dataIndex: 'updated_at', key: 'updated_at', width: 180 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120 },
    { title: 'User', dataIndex: 'user', key: 'user', width: 150 },
    { title: 'Comment For', dataIndex: 'comment_for', key: 'comment_for', width: 150 },
    { title: 'Comment', dataIndex: 'comment', key: 'comment', width: 300 },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: any, record: any) => (
        <>
          <EditOutlined
            disabled={!editable || record.status !== status}
            style={{
              marginRight: 8,
              color: record.status !== status ? 'gray' : '#1890ff',
            }}
            onClick={() => record.status === status && handleEdit(record)}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <Button
        type="primary"
        onClick={() => setIsModalVisible(true)}
        icon={<PlusOutlined />}
        disabled={!editable && user !== 'Admin'} // Allow admin users to always access the button
        size="small"
      >
        Add Comment
      </Button>

      <Table
        columns={columns}
        dataSource={commentData}
        rowKey="id"
        style={{ marginTop: 16 }}
        pagination={{ pageSize: 5 }}
        size="small"
      />

      <Modal
        title={editingComment ? 'Edit Comment' : 'Add Comment'}
        open={isModalVisible}
        onOk={handleAddOrEditComment}
        confirmLoading={isLoading}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingComment(null);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="commentFor"
            label="Comment For"
            rules={[{ required: true, message: 'Please select an option' }]}
          >
            <Select placeholder="Select">
              <Select.Option value="CUSTOMER">Customer</Select.Option>
              <Select.Option value="USER">System Admin</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="comment" label="Comment" rules={[{ required: true, message: 'Please enter a comment' }]}>
            <Input.TextArea placeholder="Enter your comment" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CommentsTab;
